import axios from "axios";
import { BASE_URL } from "../config/config";
import { getToken, getCSRFToken, getCookie, setCookie, removeCookie } from "../helpers/index";
import jwt_decode from "jwt-decode";

const instance = axios.create({
  baseURL: BASE_URL,
});

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
// Queue of failed requests to retry after token refresh
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

instance.interceptors.request.use(
  (config) => {
    const CSRF_TOKEN = getCSRFToken();
    if (CSRF_TOKEN) config.headers["X-XSRF-TOKEN"] = CSRF_TOKEN;
    config.withCredentials = true; // This will automatically pass all cookies
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only attempt refresh on 401 responses, and not on the refresh-token endpoint itself
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes("refresh-token")
    ) {
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            // Retry original request (cookies are already updated)
            return instance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getCookie("refreshToken");

      if (!refreshToken) {
        // No refresh token available — force logout
        isRefreshing = false;
        processQueue(new Error("No refresh token"), null);
        removeCookie("authToken");
        removeCookie("refreshToken");
        removeCookie("user");
        window.location.href = "/";
        return Promise.reject(error);
      }

      try {
        // Call the refresh-token endpoint
        const response = await axios.post(
          `${BASE_URL}admin/refresh-token`,
          { refreshToken },
          { withCredentials: true }
        );

        const { token: newToken, refreshToken: newRefreshToken } = response.data;

        // Update cookies with new tokens
        setCookie("authToken", newToken, 1); // 1 day
        if (newRefreshToken) {
          setCookie("refreshToken", newRefreshToken, 7); // 7 days
        }
        setCookie("user", JSON.stringify(jwt_decode(newToken)), 1);

        isRefreshing = false;
        processQueue(null, newToken);

        // Retry the original failed request
        return instance(originalRequest);
      } catch (refreshError) {
        // Refresh failed — force logout
        isRefreshing = false;
        processQueue(refreshError, null);
        removeCookie("authToken");
        removeCookie("refreshToken");
        removeCookie("user");
        window.location.href = "/";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default instance;
